// Compensation editor for ONE instructor/class-session assignment.
//
// What this control shows is exactly what payroll resolves for a meeting of
// this assignment: the same modes, the same Hub schedule, the same custom
// amount. Changes are effective-dated and never touch paid payroll.
import { useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Wallet } from "lucide-react";
import {
  COMPENSATION_MODE_LABEL,
  formatUsd,
  hubRateCents,
  resolveAssignmentCompensation,
  saveAssignmentCompensation,
  type CompensationMode,
} from "@/lib/assignmentCompensation";

interface Props {
  assignment: {
    id: string;
    compensation_mode?: string | null;
    compensation_rate_cents?: number | null;
    compensation_effective_date?: string | null;
    start_time?: string | null;
    end_time?: string | null;
  };
  onSaved?: () => void;
}

const today = () => new Date().toISOString().slice(0, 10);

export default function AssignmentCompensationEditor({ assignment, onSaved }: Props) {
  const resolved = resolveAssignmentCompensation(assignment);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState<CompensationMode>(
    (resolved.mode ?? "hub_standard") as CompensationMode,
  );
  const [dollars, setDollars] = useState<string>(
    resolved.mode === "custom_per_meeting" && resolved.rateCents != null
      ? (resolved.rateCents / 100).toFixed(2)
      : "",
  );
  const [effective, setEffective] = useState<string>(resolved.effectiveDate ?? today());

  const hubPreview = hubRateCents(resolved.durationMinutes);

  const save = async () => {
    setBusy(true);
    try {
      await saveAssignmentCompensation({
        assignmentId: assignment.id,
        mode,
        rateCents:
          mode === "custom_per_meeting" ? Math.round(Number(dollars || "NaN") * 100) : null,
        effectiveDate: effective || null,
      });
      toast.success("Compensation saved. Only unpaid meetings from the effective date change.");
      setOpen(false);
      onSaved?.();
    } catch (e: any) {
      toast.error(e?.message ?? "Couldn't save the compensation.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-2 rounded border bg-muted/30 p-2 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-xs">
          <Wallet className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="font-medium">Compensation</span>
          <Badge variant={resolved.needsReview ? "destructive" : "secondary"}>
            {resolved.label}
          </Badge>
        </div>
        <Button size="sm" variant="ghost" onClick={() => setOpen((v) => !v)}>
          {open ? "Close" : "Edit"}
        </Button>
      </div>

      {open && (
        <div className="space-y-2">
          <div className="flex flex-wrap items-end gap-2">
            <div className="min-w-[200px] flex-1">
              <Label className="text-xs">Pay arrangement</Label>
              <Select value={mode} onValueChange={(v) => setMode(v as CompensationMode)}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hub_standard">
                    {COMPENSATION_MODE_LABEL.hub_standard}
                  </SelectItem>
                  <SelectItem value="custom_per_meeting">Custom Per Meeting</SelectItem>
                  <SelectItem value="volunteer">{COMPENSATION_MODE_LABEL.volunteer}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {mode === "custom_per_meeting" && (
              <div>
                <Label className="text-xs">Amount per meeting ($)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={dollars}
                  onChange={(e) => setDollars(e.target.value)}
                  className="h-9 w-[140px]"
                  placeholder="40.00"
                />
              </div>
            )}

            <div>
              <Label className="text-xs">Effective date</Label>
              <Input
                type="date"
                value={effective}
                onChange={(e) => setEffective(e.target.value)}
                className="h-9 w-[160px]"
              />
            </div>

            <Button
              size="sm"
              disabled={busy || (mode === "custom_per_meeting" && !dollars)}
              onClick={save}
            >
              Save
            </Button>
          </div>

          <p className="text-[11px] text-muted-foreground">
            {mode === "hub_standard" &&
              (hubPreview != null
                ? `This ${resolved.durationMinutes}-minute class pays ${formatUsd(hubPreview)} per meeting on the Hub schedule (60 min $30 · 90 min $35 · 120 min $40).`
                : resolved.durationMinutes == null
                  ? "This assignment has no meeting start/end time, so payroll can't tell how long a meeting is. It will be flagged Needs Review instead of assuming 60 minutes."
                  : `This class's ${resolved.durationMinutes}-minute length isn't on the Hub schedule, so payroll will flag it for review.`)}
            {mode === "custom_per_meeting" &&
              "This exact amount is paid for each meeting of this assignment only."}
            {mode === "volunteer" &&
              "No pay is generated for this class. The instructor stays on the roster, schedule and attendance as normal, and their other paid classes are unaffected."}{" "}
            Meetings already paid keep what they were paid; only unpaid meetings on or after the
            effective date use the new arrangement.
          </p>
        </div>
      )}
    </div>
  );
}
